import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Badge } from "./ui/badge"
import { Avatar, AvatarFallback } from "./ui/avatar"
import { Button } from "./ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Checkbox } from "./ui/checkbox"
import { Plus, Save, Edit, Trash2 } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { crearUsuario, actualizarUsuario, eliminarUsuario } from "../services/pruebasService"
import { useUserRol } from "../App"
import { ROL_LABELS } from "../lib/permisos"

const ROLES_SELECCIONABLES = ['ADMIN', 'ANALISTA', 'COORDINADOR', 'GERENTE'] as const

interface Usuario {
  id: string
  nombre: string
  email: string
  rol: string
  estado: string
  ultimaConexion: string
  puesto?: string
  area?: string
  autoriza_reapertura?: boolean
}

interface EditUsuarioDialogProps {
  usuario: Usuario
  onSave: (id: string, data: Partial<Usuario>) => Promise<void>
}

function EditUsuarioDialog({ usuario, onSave }: EditUsuarioDialogProps) {
  const [nombre, setNombre] = useState(usuario.nombre)
  const [puesto, setPuesto] = useState(usuario.puesto || 'ANALISTA')
  const [area, setArea] = useState(usuario.area || '')
  const [autorizaReapertura, setAutorizaReapertura] = useState(usuario.autoriza_reapertura ?? false)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setLoading(true)
    setError(null)
    try {
      await onSave(usuario.id, { nombre, puesto, area, autoriza_reapertura: autorizaReapertura })
      setIsOpen(false)
    } catch (err: any) {
      setError(err.message || 'Error al guardar usuario.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full mt-4">
          <Edit className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Usuario</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Complete los campos para editar la información del usuario. Los permisos de cada rol se administran en la pantalla "Permisos".
        </DialogDescription>
        <div className="space-y-4">
          <div>
            <label className="text-muted-foreground">Nombre Completo</label>
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Puesto / Rol</label>
            <Select value={puesto} onValueChange={setPuesto}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES_SELECCIONABLES.map(r => (
                  <SelectItem key={r} value={r}>{ROL_LABELS[r]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-muted-foreground">Área</label>
            <Input
              value={area}
              onChange={(e) => setArea(e.target.value)}
              className="mt-1"
              placeholder="Ej: Laboratorio Físico"
            />
          </div>
          {puesto !== 'ADMIN' && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={`autoriza-reapertura-${usuario.id}`}
                checked={autorizaReapertura}
                onCheckedChange={(v) => setAutorizaReapertura(!!v)}
              />
              <label htmlFor={`autoriza-reapertura-${usuario.id}`} className="text-sm">
                Puede autorizar reaperturas sin trazabilidad
              </label>
            </div>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface AddUsuarioDialogProps {
  onAdd: (data: { nombre: string; correo: string; puesto: string; area?: string }) => Promise<void>
}

function AddUsuarioDialog({ onAdd }: AddUsuarioDialogProps) {
  const [nombre, setNombre] = useState('')
  const [correo, setCorreo] = useState('')
  const [puesto, setPuesto] = useState('ANALISTA')
  const [area, setArea] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!nombre.trim() || !correo.trim()) return
    setLoading(true)
    setError(null)
    try {
      await onAdd({
        nombre: nombre.trim(),
        correo: correo.trim().toLowerCase(),
        puesto,
        area: area.trim() || undefined,
      })
      setNombre('')
      setCorreo('')
      setPuesto('ANALISTA')
      setArea('')
      setIsOpen(false)
    } catch (err: any) {
      setError(err.message || 'Error al crear usuario.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Agregar Usuario
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar Nuevo Usuario</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Complete los campos para agregar un nuevo usuario al sistema. Los permisos de cada rol se administran en la pantalla "Permisos".
        </DialogDescription>
        <div className="space-y-4">
          <div>
            <label className="text-muted-foreground">Nombre Completo *</label>
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="mt-1"
              placeholder="Ingrese el nombre completo"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Correo electrónico *</label>
            <Input
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              className="mt-1"
              placeholder="usuario@laboratorio.com"
            />
          </div>
          <div>
            <label className="text-muted-foreground">Puesto / Rol *</label>
            <Select value={puesto} onValueChange={setPuesto}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES_SELECCIONABLES.map(r => (
                  <SelectItem key={r} value={r}>{ROL_LABELS[r]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-muted-foreground">Área</label>
            <Input
              value={area}
              onChange={(e) => setArea(e.target.value)}
              className="mt-1"
              placeholder="Ej: Laboratorio Físico"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsOpen(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!nombre.trim() || !correo.trim() || loading}
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Agregando...' : 'Agregar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface DeleteUsuarioDialogProps {
  usuario: Usuario
  onDelete: (id: string) => Promise<void>
}

function DeleteUsuarioDialog({ usuario, onDelete }: DeleteUsuarioDialogProps) {
  const [loading, setLoading] = useState(false)

  const handleDelete = async () => {
    setLoading(true)
    try {
      await onDelete(usuario.id)
      toast.success('Usuario eliminado.')
    } catch (err: any) {
      toast.error(err.message || 'Error al eliminar usuario.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full text-red-600 hover:text-red-700">
          <Trash2 className="h-4 w-4 mr-2" />
          Eliminar
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Eliminar a {usuario.nombre}?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta acción quitará a {usuario.nombre} ({usuario.email}) del sistema. Ya no podrá iniciar sesión, pero su cuenta de acceso no se elimina. Esta acción no se puede deshacer.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} disabled={loading} className="bg-red-600 hover:bg-red-700">
            {loading ? 'Eliminando...' : 'Eliminar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

interface UsuariosViewProps {
  usuarios: any[];
  onRefresh?: () => void;
}

export function UsuariosView({ usuarios, onRefresh }: UsuariosViewProps) {
  const isAdmin = useUserRol() === 'ADMIN'
  const currentEmail = ((typeof window !== 'undefined' && localStorage.getItem('userEmail')) || '').trim().toLowerCase()
  const getRolColor = (rol: string) => {
    switch (rol) {
      case "Administrador":
        return "default"
      case "Gerente de Calidad":
        return "default"
      case "Coordinador":
        return "secondary"
      default:
        return "outline"
    }
  }

  const getInitials = (nombre: string) => {
    if (!nombre || typeof nombre !== 'string' || !nombre.trim()) return '?';
    return nombre
      .split(' ')
      .filter(Boolean)
      .map(n => n[0])
      .join('')
      .toUpperCase();
  }

  const handleAddUsuario = async (data: { nombre: string; correo: string; puesto: string; area?: string }) => {
    await crearUsuario(data)
    if (onRefresh) onRefresh()
  }

  const handleSaveUsuario = async (id: string, data: Partial<Usuario>) => {
    const updateData: Parameters<typeof actualizarUsuario>[1] = {}
    if (data.nombre !== undefined) updateData.nombre = data.nombre
    if (data.puesto !== undefined) updateData.puesto = data.puesto
    if (data.area !== undefined) updateData.area = data.area
    if (data.autoriza_reapertura !== undefined) updateData.autoriza_reapertura = data.autoriza_reapertura
    await actualizarUsuario(id, updateData)
    if (onRefresh) onRefresh()
  }

  const handleDeleteUsuario = async (id: string) => {
    await eliminarUsuario(id)
    if (onRefresh) onRefresh()
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1>Usuarios</h1>
        {isAdmin && <AddUsuarioDialog onAdd={handleAddUsuario} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {usuarios.map((usuario) => (
          <Card key={usuario.id}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarFallback>{getInitials(usuario.nombre)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <CardTitle className="text-base">{usuario.nombre}</CardTitle>
                  <p className="text-muted-foreground">{usuario.email}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-muted-foreground">Rol</label>
                  <Badge variant={getRolColor(usuario.rol) as any}>
                    {usuario.rol}
                  </Badge>
                </div>
                {usuario.area && (
                  <div className="flex justify-between items-center">
                    <label className="text-muted-foreground">Área</label>
                    <span className="text-sm">{usuario.area}</span>
                  </div>
                )}
                <div>
                  <label className="text-muted-foreground">Última Conexión</label>
                  <p>{new Date(usuario.ultimaConexion).toLocaleDateString()}</p>
                </div>
                {isAdmin && (
                  <div className="space-y-2">
                    <EditUsuarioDialog
                      usuario={usuario}
                      onSave={handleSaveUsuario}
                    />
                    {usuario.email.trim().toLowerCase() !== currentEmail && (
                      <DeleteUsuarioDialog
                        usuario={usuario}
                        onDelete={handleDeleteUsuario}
                      />
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
